<nz-card class="mb-12">
  <h5>Issue Overview:</h5>
  @if (enclosureBk) {
    <p>
      <b
        >{{ storageLevelV2.String | levelIdentifierToLevelName | titlecase }} Location:
        {{ enclosureBk | componentNameTranslation }} &rarr; {{ componentBk | componentNameTranslation }}
        @if (getCellPosition()) {
          <span>
            <b>&rarr; {{ getCellPosition() }}</b>
          </span>
        }
      </b>
    </p>
    <p>
      A limiting {{ storageLevelV2.Cell | levelIdentifierToLevelName | lowercase }} has been identified in
      <b>{{ componentBk | componentNameTranslation }}</b>
      @if (getCellPosition()) {
        , specifically <b>{{ getCellPosition() }}</b>
      }
      . It limits the usable energy of this {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }}.
    </p>
  }
  @if (limitingCellDetection) {
    <div>
      <ul class="list-disc ml-12 pl-0">
        <li>
          The limiting {{ storageLevelV2.Cell | levelIdentifierToLevelName | lowercase }} was identified the first time on
          <b>{{ limitingCellDetection.firstDetectedDate | date: 'yyyy-MM-dd' }}</b
          >.
        </li>
        <li>
          The limiting {{ storageLevelV2.Cell | levelIdentifierToLevelName | lowercase }} was identified the last time on
          <b>{{ limitingCellDetection.lastDetectedDate | date: 'yyyy-MM-dd' }}</b
          >. Use the Data Explorer button below to inspect the last detected date in detail.
        </li>
        <li>
          In that time period, it was detected on <b>{{ limitingCellDetection.numberDetectedDates }}</b> days.
        </li>
      </ul>
    </div>
  }
  <p class="mt-16">
    The energy associated with this {{ storageLevelV2.Cell | levelIdentifierToLevelName | lowercase }} is currently classified as
    recoverable and corrective measures can be taken.
  </p>
</nz-card>

<nz-card class="mb-12">
  <h5>Why This Matters for You:</h5>
  <p>
    The high imbalances and the reduced total amount of usable energy in the
    {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }} are a result of one specific
    {{ storageLevelV2.Cell | levelIdentifierToLevelName | lowercase }} limiting the usable energy of the whole
    {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }}.
  </p>
  <p>Limiting {{ storageLevelV2.Cell | levelIdentifierToLevelName | lowercase }} behavior may arise from a combination of:</p>
  <ul class="list-disc ml-12 pl-0">
    <li>Abnormally high capacity fade</li>
    <li>Elevated internal resistance</li>
    <li>Thermal gradients or persistent SoC drifts</li>
    <li>Manufacturing variances</li>
  </ul>
</nz-card>

@if (hasSiblingEffect) {
  <nz-card class="mb-12">
    <h5>Sibling effect</h5>
    <p>
      The sibling effect occurs within this
      {{ storageLevelV2.DCCombiner | levelIdentifierToLevelName }}. This means, that the usable energy of this
      {{ storageLevelV2.DCCombiner | levelIdentifierToLevelName }} is determined by its worst
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }}. The usable energy of the
      {{ storageLevelV2.DCCombiner | levelIdentifierToLevelName }} is calculated by the usable energy of the worst
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }} multiplied by the number of sibling
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }}s within this
      {{ storageLevelV2.DCCombiner | levelIdentifierToLevelName }} (and not by the sum of usable energy of individual
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }}s).
    </p>
    <p>
      TWAICE recommends the "quick fix" to take the worst
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }} offline if the remaining usable energy of the worst
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }} is less than the energy that would be recovered in the
      whole {{ storageLevelV2.DCCombiner | levelIdentifierToLevelName }} by taking the worst
      {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }} offline plus an additional {{ quickFixBufferPercent }}%
      buffer.
    </p>
    <p>
      In this case, the difference is <b>below</b> the buffer of {{ quickFixBufferPercent }}%. We <b>don't recommend</b>
      the quick fix.
    </p>
  </nz-card>
}

<nz-card class="mb-12">
  <h5>Recommendation:</h5>
  <ul class="list-disc ml-12 pl-0">
    <li>
      Balancing of that {{ storageLevelV2.String | levelIdentifierToLevelName | lowercase }} might resolve the issue, but
      potentially will not increase the usable energy long term because of the limiting
      {{ storageLevelV2.Cell | levelIdentifierToLevelName | lowercase }}.
    </li>

    <li>
      Replacement of the
      {{ storageLevelV2.StringModule | levelIdentifierToLevelName | lowercase }}
      @if (getCellPosition()) {
        <span>
          containing <b>{{ getCellPosition() }}</b></span
        >
      }
      might be necessary to recover the energy long term.
    </li>
  </ul>
</nz-card>

<nz-card class="mb-12">
  <h5>How TWAICE Detects Limiting Cells:</h5>
  <p>TWAICE analyzes 24h windows of data for every string.</p>
  <p>For every 24h window, a cell is classified as "limiting cell" if the following conditions were met:</p>
  <ul class="list-disc ml-12 pl-0">
    <li>
      One specific cell represents the minimum-voltage location at least 80% of the time, when the string was operated
      <ul class="list-disc ml-12 pl-0">
        <li>below 30% SoC, and</li>
        <li>with a voltage spread greater than 10mV.</li>
      </ul>
    </li>

    <li>This string has a median SoC spread greater than 10%.</li>
    @if (limitingCellDetection) {
      <li>This specific cell was detected the first and last time on the dates mentioned above.</li>
    }
  </ul>
  <p class="mt-16">To reduce noise and further stabilize the detection, we consecutively apply a post-processing:</p>
  <ul class="list-disc ml-12 pl-0">
    <li>
      A limiting cell needs to be detected within four out of the last seven 24h windows during which this string was operated
      below 30% SoC.
    </li>
  </ul>
</nz-card>
